import React, { useState, useEffect, useCallback } from "react";
import { Container, Row, Col, Button } from "react-bootstrap";
import { Menu } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

// Importar componentes
import VideoPlayer from "./videoPlayer";
import ExerciseList from "./exerciseList";
import Sidebar from "./sidebar";
import MetricsView from "./metricsView";
import ReportProblem from "./reportProblem";
import MyTickets from "./myTickets";
import { registerAndSubscribe } from "../../serviceWorkerRegistration";

// Importar API
import {
  getRoutinesByClientAndDay,
  getRoutineCountByDay,
  CreateRoutineLogUser,
} from "../../api/apiClient";

const MoveApp = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  // Obtener email del usuario autenticado desde sessionStorage
  const userEmail = sessionStorage.getItem("userEmail");
  const [user, setUser] = useState({ email: userEmail || "" });

  const [currentView, setCurrentView] = useState("routines");
  const [exerciseData, setExerciseData] = useState([]);
  const [selectedExercise, setSelectedExercise] = useState(null);
  const [completedExercises, setCompletedExercises] = useState([]);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [isSubscribed, setIsSubscribed] = useState(null); // null = unknown, false = not subscribed, true = subscribed
  const [layoutMode, setLayoutMode] = useState("horizontal"); // "horizontal" (default) o "vertical" (YouTube style)
  const [routineCounts, setRoutineCounts] = useState(null);

  // Obtener fecha local en formato YYYY-MM-DD
  const getLocalDateString = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, "0");
    const day = String(now.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  // Función para determinar el rango de tiempo (mañana/tarde)
  const getCurrentTimeRange = () => {
    const currentHour = new Date().getHours();
    return currentHour < 12 ? 0 : 1; // 0 = mañana, 1 = tarde
  };

  // Función para procesar y aplanar los datos del API
  const processRoutineData = (routines) => {
    const currentTimeRange = getCurrentTimeRange();
    const processedVideos = [];

    routines.forEach((routine) => {
      // Buscar el timeRange correcto (mañana o tarde) y también mixto (2 = todo el día)
      const matchingTimeRanges = routine.routineVideos.filter(
        (rv) => rv.timeRange === currentTimeRange || rv.timeRange === 2,
      );

      matchingTimeRanges.forEach((timeRangeData) => {
        if (timeRangeData.videos) {
          timeRangeData.videos.forEach((video) => {
            processedVideos.push({
              ...video,
              routineName: routine.name,
              routineDescription: routine.description,
              idRoutine: routine.idRoutine,
              timeRange: timeRangeData.timeRange,
            });
          });
        }
      });
    });

    return processedVideos;
  };

  // Cargar rutinas del API al montar el componente
  useEffect(() => {
    const loadRoutines = async () => {
      try {
        const clientId = sessionStorage.getItem("clientId");
        const idUser = sessionStorage.getItem("idUser");
        const userEmailStorage = sessionStorage.getItem("userEmail");

        // Validar que el usuario esté autenticado
        if (!userEmailStorage || !clientId || !idUser) {
          console.warn("Usuario no autenticado, redirigiendo al login");
          navigate("/");
          return;
        }

        // Actualizar el estado del usuario si cambió en sessionStorage
        if (userEmailStorage !== user.email) {
          setUser({ email: userEmailStorage });
        }

        // Calcular el día (JavaScript: 0=domingo, pero API espera 1=domingo)
        const jsDay = new Date().getDay();
        const currentDay = jsDay === 0 ? 1 : jsDay + 1;

        // Calcular timeRange (0 = mañana, 1 = tarde)
        const currentHour = new Date().getHours();
        const timeRange = currentHour < 12 ? 0 : 1;

        const dateNow = getLocalDateString();

        console.log("Parámetros API:", {
          clientId,
          idUser,
          currentDay,
          timeRange,
          dateNow,
        });

        if (clientId && idUser) {
          // Llamar ambos endpoints en paralelo
          const [response, countsResponse] = await Promise.all([
            getRoutinesByClientAndDay(
              clientId,
              idUser,
              currentDay,
              timeRange,
              dateNow,
            ),
            getRoutineCountByDay(clientId, idUser, currentDay, dateNow),
          ]);

          if (response && response.length > 0) {
            // Procesar y aplanar los datos
            const processedData = processRoutineData(response);

            if (processedData.length > 0) {
              setExerciseData(processedData);
              setSelectedExercise(processedData[0]); // Seleccionar el primer ejercicio

              // Inicializar completedExercises con los videos que ya vienen marcados como completados del API
              const alreadyCompleted = processedData
                .filter((video) => video.isCompleted === true)
                .map((video) => video.idVideo);
              setCompletedExercises(alreadyCompleted);
            }
          }

          setRoutineCounts(countsResponse || []);
        }
      } catch (error) {
        console.error("Error cargando rutinas:", error);
      } finally {
        setLoading(false);
      }
    };

    loadRoutines();
  }, []);

  // Check existing push subscription to show/hide the register button
  useEffect(() => {
    const checkSubscription = async () => {
      try {
        if (!("serviceWorker" in navigator) || !("PushManager" in window)) {
          setIsSubscribed(false);
          return;
        }

        const registration = await navigator.serviceWorker.getRegistration();
        if (!registration) {
          setIsSubscribed(false);
          return;
        }

        const subscription = await registration.pushManager.getSubscription();
        setIsSubscribed(!!subscription);
      } catch (err) {
        console.warn("Error checking push subscription", err);
        setIsSubscribed(false);
      }
    };

    checkSubscription();
  }, []);

  const handleCloseSidebar = useCallback(() => setSidebarOpen(false), []);

  const handleLogout = useCallback(() => {
    setUser(null);
    setCurrentView("routines");
    setCompletedExercises([]);
    setSidebarOpen(false);
    navigate("/");
  }, [navigate]);

  const handleVideoComplete = async (exerciseId) => {
    console.log("Entro");
    if (!completedExercises.includes(exerciseId)) {
      setCompletedExercises((prev) => [...prev, exerciseId]);

      // Actualizar el estado isCompleted en exerciseData
      setExerciseData((prevData) =>
        prevData.map((ex) =>
          ex.idVideo === exerciseId ? { ...ex, isCompleted: true } : ex,
        ),
      );

      // Registrar la actividad en el API
      try {
        const clientId = sessionStorage.getItem("clientId");
        const emailUser = user?.email || sessionStorage.getItem("userEmail");

        // Encontrar el ejercicio completado para obtener idRoutine y timeRange
        const completedExercise = exerciseData.find(
          (ex) => ex.idVideo === exerciseId,
        );

        console.log("Ejercicio completado encontrado:", completedExercise); // DEBUG: Ver objeto completo
        console.log(
          "Duration del ejercicio completado:",
          completedExercise?.duration,
        ); // DEBUG: Ver duration específicamente

        console.log(
          parseInt(clientId),
          emailUser,
          completedExercise.idRoutine,
          exerciseId,
          true,
          completedExercise.timeRange,
          completedExercise.duration,
        );

        if (clientId && emailUser && completedExercise) {
          const response = await CreateRoutineLogUser(
            parseInt(clientId),
            emailUser,
            completedExercise.idRoutine,
            exerciseId,
            true,
            completedExercise.timeRange,
            completedExercise.duration,
          );

          if (response.success) {
            console.log("Actividad registrada exitosamente:", response.data);
          } else {
            console.error("Error al registrar actividad:", response.message);
          }
        }
      } catch (error) {
        console.error("Error al registrar actividad completada:", error);
      }
    }
  };

  const handleNavigation = useCallback(
    (view) => {
      if (view === "logout") {
        handleLogout();
      } else {
        setCurrentView(view);
        setSidebarOpen(false);
      }
    },
    [handleLogout],
  );

  // Función para ir al siguiente video
  const handleNextVideo = () => {
    if (!selectedExercise || exerciseData.length === 0) return;

    const currentIndex = exerciseData.findIndex(
      (ex) => ex.idVideo === selectedExercise.idVideo,
    );

    if (currentIndex !== -1 && currentIndex < exerciseData.length - 1) {
      setSelectedExercise(exerciseData[currentIndex + 1]);
    }
  };

  // Verificar si hay un siguiente video
  const hasNextVideo = () => {
    if (!selectedExercise || exerciseData.length === 0) return false;

    const currentIndex = exerciseData.findIndex(
      (ex) => ex.idVideo === selectedExercise.idVideo,
    );

    return currentIndex !== -1 && currentIndex < exerciseData.length - 1;
  };

  // Función para ir al video anterior
  const handlePreviousVideo = () => {
    if (!selectedExercise || exerciseData.length === 0) return;

    const currentIndex = exerciseData.findIndex(
      (ex) => ex.idVideo === selectedExercise.idVideo,
    );

    if (currentIndex > 0) {
      setSelectedExercise(exerciseData[currentIndex - 1]);
    }
  };

  // Verificar si hay un video anterior
  const hasPreviousVideo = () => {
    if (!selectedExercise || exerciseData.length === 0) return false;

    const currentIndex = exerciseData.findIndex(
      (ex) => ex.idVideo === selectedExercise.idVideo,
    );

    return currentIndex > 0;
  };

  // Función para cambiar el modo de layout
  const toggleLayoutMode = useCallback(() => {
    setLayoutMode((prevMode) =>
      prevMode === "horizontal" ? "vertical" : "horizontal",
    );
  }, []);

  // Handler to register notifications, will be passed to Sidebar
  const handleRegisterNotifications = useCallback(async () => {
    try {
      const idUser = sessionStorage.getItem("idUser");
      if (!idUser) {
        console.error(
          "No existe idUser en sessionStorage. Imposible registrar.",
        );
        alert(
          "No se pudo obtener su identificador de usuario. Inicie sesión nuevamente",
        );
        return;
      }
      await registerAndSubscribe({ userId: idUser });
      setIsSubscribed(true);
    } catch (err) {
      console.error("Registro de notificaciones falló:", err);
    }
  }, []);

  return (
    <div
      className="min-vh-100"
      style={{
        background:
          "linear-gradient(135deg, #10b981 0%, #14b8a6 50%, #06b6d4 100%)",
      }}
    >
      {/* Header */}
      <Container fluid>
        <Row className="align-items-center p-3">
          <Col xs="auto" style={{ width: "56px" }}>
            <Button
              variant="link"
              className="text-white p-2"
              onClick={() => setSidebarOpen(true)}
              style={{ textDecoration: "none" }}
            >
              <Menu size={24} />
            </Button>
          </Col>

          <Col className="text-center">
            <h1
              className="text-white mb-0"
              style={{
                fontFamily: "Kodchasan, sans-serif",
                fontSize: "3rem",
                fontWeight: "800",
                opacity: 0.6,
                letterSpacing: "1px",
              }}
            >
              Move!
            </h1>
          </Col>

          <Col xs="auto" style={{ width: "56px" }}>
            {/* Spacer para balancear el header */}
          </Col>
        </Row>
      </Container>

      {/* Sidebar */}
      <Sidebar
        user={user}
        isOpen={sidebarOpen}
        onClose={handleCloseSidebar}
        onNavigate={handleNavigation}
        currentView={currentView}
        isSubscribed={isSubscribed}
        onRegisterNotifications={handleRegisterNotifications}
        layoutMode={layoutMode}
        onToggleLayout={toggleLayoutMode}
        routineCounts={routineCounts}
      />

      {/* Contenido principal */}
      {currentView === "report" ? (
        <ReportProblem />
      ) : currentView === "metrics" ? (
        <MetricsView
          completedExercises={completedExercises}
          exerciseData={exerciseData}
        />
      ) : currentView === "tickets" ? (
        <MyTickets />
      ) : (
        <div>
          {loading ? (
            <Container className="text-center py-5">
              <div className="text-white">
                <h3>{t("move.loadingRoutines")}</h3>
              </div>
            </Container>
          ) : exerciseData.length === 0 ? (
            <Container className="text-center py-5">
              <div className="text-white">
                <h3>{t("move.noRoutinesAvailable")}</h3>
                <p>{t("move.tryAgainLater")}</p>
              </div>
            </Container>
          ) : (
            <div
              style={{ width: "100%", maxWidth: "100vw", overflowX: "hidden" }}
            >
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns:
                    layoutMode === "horizontal" ? "1fr" : "repeat(12, 1fr)",
                  gap: layoutMode === "horizontal" ? "0" : "1rem",
                  padding: layoutMode === "horizontal" ? "0" : "0 1rem",
                  maxWidth: "100%",
                }}
              >
                {/* Video Player - Always rendered in same position */}
                <div
                  style={{
                    gridColumn:
                      layoutMode === "horizontal" ? "1" : "1 / span 8",
                    order: 1,
                    minWidth: 0,
                  }}
                >
                  <VideoPlayer
                    exercise={selectedExercise}
                    onVideoComplete={handleVideoComplete}
                    isCompleted={completedExercises.includes(
                      selectedExercise?.idVideo,
                    )}
                    onNextVideo={handleNextVideo}
                    hasNextVideo={hasNextVideo()}
                    onPreviousVideo={handlePreviousVideo}
                    hasPreviousVideo={hasPreviousVideo()}
                    layoutMode={layoutMode}
                  />
                </div>

                {/* Exercise List - Always rendered in same position */}
                <div
                  style={{
                    gridColumn:
                      layoutMode === "horizontal" ? "1" : "9 / span 4",
                    order: 2,
                    minWidth: 0,
                  }}
                >
                  <ExerciseList
                    exercises={exerciseData}
                    selectedExercise={selectedExercise}
                    onSelectExercise={setSelectedExercise}
                    completedExercises={completedExercises}
                    layoutMode={layoutMode}
                    onToggleLayout={toggleLayoutMode}
                  />
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default MoveApp;
